// paymentBoard — Firestore backing for the Payment Approval kanban
// (mail-order department: long-distance orders paid by Interac e-Transfer).
//
// One doc per order card in the `paymentBoard` collection. The page
// subscribes in real time, so drags/edits sync across every open console.
// Stage flow: pending (awaiting e-Transfer) → processing (verifying)
// → completed (paid & dispatched — moving here offers the "payment
// received" email via services/emailApi.sendEmail).

import { BOARD_DAYS, HISTORY_TEST_MINUTES, historyCutoffIso } from './boardHistory';
import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import { FEATURES, MAIN_PARITY } from './features';
import { claimFromRow, type PaymentClaim } from './paymentClaims';
import { USE_SUPABASE, supabase, subscribeTable, subscribeRow, msFromISO0, isoFromMs, numOr0, fetchAllRows, type ExternalTableControl } from './supabaseClient';
import { joinScopedChannel } from './live/scopedChannel';
import { EmailRecord, EmailFolder } from './emailApi';
import { loadCachedEmails, syncFolder, reassignThread, releaseThread, EMAIL_LIST_COLS } from './emailCache';

// Full pipeline, left → right on the board. `completed` predates the
// packing/shipping/delivered columns and still means "payment cleared", so the
// key is kept as-is — existing docs keep working without a migration.
// 2026-09-17 (Umer): 'processing' is gone for good — a row still parked there
// is shown under Pending by the board; 'claimed' is still WRITTEN by Claim but
// its column is commented out of the board; 'print_label' sits between packing
// and shipping. The column is plain text in Postgres, so no migration.
// 2026-09-19 (Umer): 'unassigned' has its own columns again (one per order
// kind) and is where a new card lands; 'cancelled' is a new parking stage —
// a record that the order is off the board, not a pipeline step.
export type PaymentStage =
  | 'unassigned'
  | 'claimed'
  | 'pending'
  | 'completed'
  | 'packing'
  | 'handed_to_driver' // same-day: the rider has it (2026-09-18)
  | 'returning_to_store' // same-day (0104): the order's driver is bringing it back to the store
  | 'print_label'
  | 'shipping'
  | 'delivered'
  | 'cancelled'       // off the board, kept for the record (2026-09-19)
  // "Customer says paid" (0146, 2026-11-02): the customer sent the e-Transfer form; only that form moves a card
  // here (payment_claim_submit), and Payment Received or Reject moves it on. Not paid yet.
  | 'payment_claimed'
  // Night Time Deliveries (0096, 2026-09-25): a Same Day card that goes out tonight. Parked in its own
  // column, not an order yet — Confirm makes the order (0095 link). Only with FEATURES.nightOrders.
  | 'night';
// 'crypto' (2026-10-08): the website's "Crypto (PayerURL)" checkout — paid online like a card,
// so it rides the e-transfer side of every rule that only singles out cash.
export type PaymentMethod = 'etransfer' | 'card' | 'cash' | 'crypto';

// Where a NEW card lands (Umer, 2026-09-19): back to Unassigned. Arrivals
// collect there — one column per order kind — and a dispatcher moves the card
// to Pending, which is what sends the customer their payment instructions.
// (It was briefly 'pending' on 2026-09-18; that skipped the arrival email,
// because nothing ever MOVED a card into Pending any more.)
export const NEW_CARD_STAGE: PaymentStage = 'unassigned';

// A card the email sync minted that no human has touched yet: unowned and
// still in its arrival column (Unassigned; cards minted on 2026-09-18/19 sat
// in Pending, so both count).
export const isUntouchedSyncCard = (c: Pick<BoardCard, 'createdBy' | 'stage' | 'assignedTo'>): boolean =>
  c.createdBy === 'email-sync' && !c.assignedTo &&
  (c.stage === 'unassigned' || c.stage === 'pending');

// What the Order Board LISTS (Umer, 2026-09-18, evening): EVERY order placed
// from the go-live moment on — mail AND same-day, each kind in its own set of
// columns ("Mail Order – …" / "Same Day Order – …"). The type still decides
// the column, never whether the card is shown.
//
// The cut-off is PRODUCTION-ONLY by default (main-parity sites): staging and
// local dev keep listing every card so old data stays testable — asked for
// the same day the cut-off hid staging's board. VITE_BOARD_SINCE (an ISO
// timestamp) overrides either default per site.
export const BOARD_SINCE_MS = Date.parse(
  // Go-live moment = when the board reached production: 17 Sep 2026, 15:40
  // Ottawa (EDT). Only orders placed from then on are listed (Umer: "just
  // those since we moved this feature to production").
  import.meta.env.VITE_BOARD_SINCE || (MAIN_PARITY ? '2026-09-17T19:40:00Z' : '2000-01-01T00:00:00Z')
);
// PRODUCTION hides same-day cards (Umer, 2026-09-19: "remove all the same-day
// orders from the order board" — hidden, not deleted; the rows and their
// activity stay in the database). Staging and local keep listing both kinds
// so the Same Day lanes can be tested. VITE_BOARD_HIDE_SAME_DAY ('true' /
// 'false') overrides the default per site.
// 2026-09-19 01:20 (Umer): "show same-day cards again on the prod" — listed
// everywhere by default; VITE_BOARD_HIDE_SAME_DAY='true' on a site hides them.
export const BOARD_HIDE_SAME_DAY = (import.meta.env.VITE_BOARD_HIDE_SAME_DAY ?? 'false') === 'true';

export function listedOnBoard(
  c: Pick<BoardCard, 'orderType' | 'placedAt' | 'paymentMethod'>,
  sinceMs: number = BOARD_SINCE_MS,
  hideSameDay: boolean = BOARD_HIDE_SAME_DAY
): boolean {
  if (hideSameDay && c.orderType === 'delivery') return false;
  return (c.placedAt || 0) >= sinceMs;
}

export interface SentEmailLog {
  templateKey: string;
  to: string;
  at: number; // ms epoch
  // The RFC Message-ID this send got back, plus the subject line used. Kept so
  // the NEXT email about this order can reference it and land in the same
  // conversation instead of arriving as a brand-new message (see threadEmail).
  messageId?: string;
  subject?: string;
}

// The card's diary — one entry per meaningful action, so "who did what, when"
// survives on the order itself (the Oversight page shows the present; this is
// the past). Appended via arrayUnion by logCardActivity; entries are never
// edited or removed.
export type CardActivityKind =
  | 'created'
  | 'claimed'
  | 'released'
  | 'assigned'
  | 'stage'
  | 'email_sent'
  | 'note'
  // Posted to a chat group from the popup (UPE-125): note = the group's name.
  | 'shared';

export interface CardActivity {
  at: number; // ms epoch
  by: string; // user id ('' when the actor is a system job like email-sync)
  byName: string; // display name shown in the diary
  kind: CardActivityKind;
  from?: PaymentStage; // stage moves only
  to?: PaymentStage;
  note?: string; // note text, assignee name, or "template → recipient"
  // Address edits only (client, 2026-09-22): the address the card had BEFORE
  // this change, so the popup can show "Previous: …" under the current one.
  prevAddress?: string;
  // Shares only: the room the card was posted to, so "already sent to this
  // group" can be answered without parsing the note (services/boardShare.ts).
  roomId?: string;
}

export interface BoardParcel {
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  weightKg: number;
  enteredAt?: number;   // ms epoch
  enteredBy?: string;   // user name
}

// The Canada Post shipment made FROM the card (0125, Task 14, 2026-10-09): what Canada Post answered
// when the Print Label dialog created the label. null/absent = no label made here (the tracking
// number may still have been pasted in by hand).
export interface BoardLabel {
  shipmentId: string;
  trackingPin: string;
  serviceCode: string;   // DOM.XP …
  serviceName: string;   // Xpresspost …
  labelUrl: string;      // Canada Post's artifact link — re-fetched by canada-post-label-pdf.js to print again
  chargedAmount: number | null;
  environment: 'test' | 'production';
  format: '4x6' | '8.5x11';
  createdAt: number;     // ms epoch
  createdBy: string;     // user name
}

// The latest Canada Post tracking answer for the card's tracking number (0125): written by the
// scheduled check (canada-post-track-cron.js) and by "Check now" in the popup.
export type TrackingStatus = 'delivered' | 'in_transit' | 'not_found' | 'unknown';
export interface BoardTracking {
  status: TrackingStatus;
  description: string;
  eventAt: number | null;        // ms epoch of the latest scan
  location: string;
  expectedDelivery: string | null; // YYYY-MM-DD
  deliveredAt: number | null;    // ms epoch
  checkedAt: number;             // ms epoch of the check itself
  error: string | null;          // the check's own failure, when it could not ask
}

export interface BoardCard {
  id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string; // '' until known — email sends stay disabled without it
  phone: string;
  city: string;
  distanceKm: number;
  total: number;
  itemsSummary: string;
  placedAt: number; // ms epoch
  stage: PaymentStage;
  // One owner per card, same rule as the Email inbox: claiming it takes it out
  // of everyone else's queue. Null/absent = still unassigned.
  assignedTo?: string | null;
  assignedToName?: string | null;
  claimedAt?: number | null;
  address?: string; // full delivery address (website order emails carry it)
  eTransferRef?: string;
  trackingNumber?: string; // Canada Post — set when the card moves to Shipping
  // Package dimensions + weight (0083, 2026-09-23): asked for on the move from
  // Packing to Label Generation, prefilled on the Shipping Labels page.
  // Absent/null = the dispatcher skipped it.
  parcel?: BoardParcel | null;
  label?: BoardLabel | null;       // 0125
  tracking?: BoardTracking | null; // 0125
  paidAt?: number | null;
  paymentMethod?: PaymentMethod;
  // Mirrors Order.orderType: absent/'delivery' = same-day driver run,
  // 'shipping' = city-to-city mail order (store packs and mails it).
  orderType?: 'delivery' | 'shipping';
  emailsSent?: SentEmailLog[];
  activity?: CardActivity[];
  createdBy?: string;
  createdAt?: number;
  // 0071 (2026-09-21). Both are written by the database, never by the client:
  // delivered_at is stamped by trigger on the move into Delivered, archived is
  // flipped by cron 24 hours later. rowFromPatch does not know them on purpose.
  deliveredAt?: number | null;
  archived?: boolean;
  /** 0105 (2026-09-29): when the card went to the Archive column (by hand or by the morning job). */
  archivedAt?: number;
  /** 0113 (TASK-80, 2026-10-05): when the card moved into Cancelled — stamped by the database on the move,
   *  cleared on Put back. A cancelled Mail card leaves for the Archive two days later
   *  (`services/boardCancelled.ts` says when; the morning sweep does it). */
  cancelledAt?: number | null;
  /** 0145 (2026-10-30): when the automatic payment reminder was taken for this card — written by the scheduled job
   *  (payment-reminder-cron.js) only; rowFromPatch does not know it on purpose. The Follow up flag counts from it. */
  paymentReminderAt?: number | null;
  // 0095 (2026-09-24): the order created from this card on the Orders screen. Written by the
  // orders trigger only; rowFromPatch does not know them on purpose.
  linkedOrderId?: string;
  linkedOrderNumber?: string;
  // 0096 (2026-09-25): the NIGHT tag — a Same Day order that goes out at night. Stays on the card as it
  // moves on. Since 0098 it is shared with the card's order (either side's Add / Remove changes both).
  isNight?: boolean;
  /** 0098: the Nighttime group's reference for a pasted order ("G4 · 12") — display only. */
  nightRef?: string;
  /** 0146: the card's "Customer says paid" link token (/pay/<token>) — made by the database, never written here. */
  payToken?: string;
  /** 0146: the card's latest claim (the pending one first) — written by the database only. */
  paymentClaim?: PaymentClaim | null;
}

// The normalisation 0071's `order_key` column applies — uppercase, alphanumerics
// only — so the app and the database agree on which order a card is for.
// City of a one-line Canadian address — the segment before the two-letter
// province ("…, Sudbury, ON, P3C 5H2" → "Sudbury"). Accent-aware so Québec
// cities parse. Used by the email parser AND by the popup's address editor, so
// a hand-corrected address moves the card's city line the same way a parsed
// one would (client, 2026-09-22). Undefined when the shape isn't there.
export const cityFromAddress = (a: string): string | undefined =>
  a.match(/,\s*([A-Za-zÀ-ÿ .'’-]{2,30}),\s*[A-Z]{2}\b/)?.[1]?.trim();

export const orderKeyOf = (n: string | null | undefined): string =>
  (n || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

// "0071 is not applied on this database yet." PostgREST answers a filter or an
// ON CONFLICT on a column it does not have with a 400 whose message names it.
// Every 0071-dependent read below has an older, slower path to fall back to,
// so a deploy that lands before the migration is slower, never broken.
export const isSchemaGap = (e: unknown): boolean =>
  /does not exist|42703|42P10|no unique or exclusion constraint/i.test(String((e as any)?.message || e || ''));

const COL = 'paymentBoard';

// ── Supabase seam (supabase-staging) ───────────────────────────────────────
// Table `payment_board` mirrors the Firestore collection; columns are
// snake_case, timestamps are timestamptz (the app speaks epoch-ms — always
// convert). `emails_sent` / `activity` are jsonb arrays; Postgres has no
// arrayUnion, so appends are read-modify-write.
const TABLE = 'payment_board';

function cardFromRow(r: any): BoardCard {
  return {
    id: r.id,
    orderNumber: r.order_number ?? '',
    customerName: r.customer_name ?? '',
    customerEmail: r.customer_email ?? '',
    phone: r.phone ?? '',
    city: r.city ?? '',
    distanceKm: numOr0(r.distance_km),
    total: numOr0(r.total),
    itemsSummary: r.items_summary ?? '',
    placedAt: msFromISO0(r.placed_at),
    stage: (r.stage ?? 'unassigned') as PaymentStage,
    assignedTo: r.assigned_to ?? null,
    assignedToName: r.assigned_to_name ?? null,
    claimedAt: r.claimed_at == null ? null : msFromISO0(r.claimed_at),
    // Optional fields stay ABSENT when the column is null — Firestore docs
    // simply lack them, and callers key off presence/truthiness the same way.
    ...(r.address != null ? { address: r.address } : {}),
    ...(r.etransfer_ref != null ? { eTransferRef: r.etransfer_ref } : {}),
    ...(r.tracking_number != null ? { trackingNumber: r.tracking_number } : {}),
    ...(r.parcel != null ? { parcel: r.parcel as BoardParcel } : {}),
    ...(r.label != null ? { label: r.label as BoardLabel } : {}),
    ...(r.tracking != null ? { tracking: r.tracking as BoardTracking } : {}),
    ...(r.paid_at != null ? { paidAt: msFromISO0(r.paid_at) } : {}),
    ...(r.payment_method != null ? { paymentMethod: r.payment_method as PaymentMethod } : {}),
    ...(r.order_type != null ? { orderType: r.order_type as 'delivery' | 'shipping' } : {}),
    ...(Array.isArray(r.emails_sent) ? { emailsSent: r.emails_sent as SentEmailLog[] } : {}),
    ...(Array.isArray(r.activity) ? { activity: r.activity as CardActivity[] } : {}),
    ...(r.created_by != null ? { createdBy: r.created_by } : {}),
    ...(r.created_at != null ? { createdAt: msFromISO0(r.created_at) } : {}),
    ...(r.delivered_at != null ? { deliveredAt: msFromISO0(r.delivered_at) } : {}),
    ...(r.archived != null ? { archived: !!r.archived } : {}),
    ...(r.archived_at != null ? { archivedAt: msFromISO0(r.archived_at) } : {}),
    ...(r.cancelled_at != null ? { cancelledAt: msFromISO0(r.cancelled_at) } : {}),
    ...(r.payment_reminder_at != null ? { paymentReminderAt: msFromISO0(r.payment_reminder_at) } : {}),
    ...(r.linked_order_id != null ? { linkedOrderId: r.linked_order_id } : {}),
    ...(r.linked_order_number != null ? { linkedOrderNumber: r.linked_order_number } : {}),
    ...(r.is_night ? { isNight: true } : {}),
    ...(r.night_ref ? { nightRef: String(r.night_ref) } : {}),
    ...(r.pay_token ? { payToken: String(r.pay_token) } : {}),
    ...(r.payment_claim != null ? { paymentClaim: claimFromRow(r.payment_claim) } : {}),
  };
}

// Patch → row: only the keys present in the patch become columns, so a partial
// update never clobbers other fields. Explicit nulls pass through (they clear
// columns, exactly like the Firestore null writes).
function rowFromPatch(p: Partial<Omit<BoardCard, 'id'>>): Record<string, unknown> {
  const r: Record<string, unknown> = {};
  if ('orderNumber' in p) r.order_number = p.orderNumber;
  if ('customerName' in p) r.customer_name = p.customerName;
  if ('customerEmail' in p) r.customer_email = p.customerEmail;
  if ('phone' in p) r.phone = p.phone;
  if ('city' in p) r.city = p.city;
  if ('distanceKm' in p) r.distance_km = p.distanceKm;
  if ('total' in p) r.total = p.total;
  if ('itemsSummary' in p) r.items_summary = p.itemsSummary;
  if ('placedAt' in p) r.placed_at = isoFromMs(p.placedAt);
  if ('stage' in p) r.stage = p.stage;
  if ('assignedTo' in p) r.assigned_to = p.assignedTo;
  if ('assignedToName' in p) r.assigned_to_name = p.assignedToName;
  if ('claimedAt' in p) r.claimed_at = isoFromMs(p.claimedAt);
  if ('address' in p) r.address = p.address;
  if ('eTransferRef' in p) r.etransfer_ref = p.eTransferRef;
  if ('trackingNumber' in p) r.tracking_number = p.trackingNumber;
  if ('parcel' in p) r.parcel = p.parcel ?? null;
  if ('label' in p) r.label = p.label ?? null;
  if ('tracking' in p) r.tracking = p.tracking ?? null;
  if ('paidAt' in p) r.paid_at = isoFromMs(p.paidAt);
  if ('paymentMethod' in p) r.payment_method = p.paymentMethod;
  if ('orderType' in p) r.order_type = p.orderType;
  if ('emailsSent' in p) r.emails_sent = p.emailsSent;
  if ('activity' in p) r.activity = p.activity;
  if ('createdBy' in p) r.created_by = p.createdBy;
  if ('createdAt' in p) r.created_at = isoFromMs(p.createdAt);
  if ('isNight' in p) r.is_night = !!p.isNight;   // 0096
  if ('nightRef' in p) r.night_ref = p.nightRef || null;   // 0098
  return r;
}

// emails-cache row → the EmailRecord shape the parsers expect (from_addr→from,
// to_addr→to, refs→references, ISO date→ms).
function emailFromRow(r: any): EmailRecord {
  return {
    uid: numOr0(r.uid),
    messageId: r.message_id ?? '',
    folder: r.folder as EmailFolder,
    from: r.from_addr ?? '',
    fromName: r.from_name ?? '',
    to: r.to_addr ?? '',
    subject: r.subject ?? '',
    date: msFromISO0(r.date),
    snippet: r.snippet ?? '',
    bodyText: r.body_text ?? '',
    bodyHtml: r.body_html ?? '',
    read: !!r.read,
    ...(r.in_reply_to != null ? { inReplyTo: r.in_reply_to } : {}),
    ...(r.refs != null ? { references: r.refs as string[] } : {}),
    assignedTo: r.assigned_to ?? null,
    assignedToName: r.assigned_to_name ?? null,
    claimedAt: r.claimed_at == null ? null : msFromISO0(r.claimed_at),
  };
}

// ── live data v2: the board on `team:board` (FEATURES.liveV2Board) ──────────
// Migration 0077 makes the DATABASE publish `card_changed {card_id, op}` to a
// private Broadcast topic — ids only, no customer data on the wire. A hint is
// answered by fetching that ONE row through the normal API (row security is
// checked there exactly as today) and patching the list; a re-join after a
// drop re-reads the list. No postgres_changes binding on payment_board at all.
export const BOARD_TOPIC = 'team:board';
export const BOARD_EVENT = 'card_changed';
// Hints are gathered for a moment so a burst (the daily archive run touches
// hundreds of cards in one statement) is one request, not one per card — and
// past this many ids it is cheaper and safer to re-read the list.
const BOARD_HINT_WINDOW_MS = 250;
const BOARD_HINT_BURST_MAX = 25;

/** Drives one subscribeTable list from `team:board` hints. `onUnavailable`
 *  fires when the channel cannot be joined (migration missing, role refused). */
function boardHintDriver(select: string, onUnavailable: () => void) {
  return (ctl: ExternalTableControl): (() => void) => {
    let dead = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const pending = new Set<string>();
    // A fetch already in flight can come back with a card that was deleted
    // meanwhile; the DELETE hint wins.
    const deleted = new Set<string>();
    const flush = async () => {
      timer = null;
      const ids = [...pending];
      pending.clear();
      if (dead || ids.length === 0) return;
      if (ids.length > BOARD_HINT_BURST_MAX) { ctl.refetch(); return; }
      try {
        const q = supabase().from(TABLE).select(select);
        const { data, error } = await (ids.length === 1 ? q.eq('id', ids[0]) : q.in('id', ids));
        if (dead) return;
        if (error) { ctl.refetch(); return; }
        const found = new Set<string>();
        for (const row of (data as any[]) || []) {
          if (row?.id == null || deleted.has(String(row.id))) continue;
          found.add(String(row.id));
          ctl.change({ eventType: 'UPDATE', new: row });
        }
        // Not returned = deleted, or no longer visible to this user.
        for (const id of ids) if (!found.has(id)) ctl.change({ eventType: 'DELETE', old: { id } });
      } catch {
        if (!dead) ctl.refetch(); // background read — never surfaces
      }
    };
    const leave = joinScopedChannel(BOARD_TOPIC, [BOARD_EVENT], {
      onEvent: (_event, payload) => {
        if (dead) return;
        const id = payload?.card_id == null ? '' : String(payload.card_id);
        if (!id) { ctl.refetch(); return; }
        if (payload?.op === 'DELETE') {
          pending.delete(id);
          deleted.add(id);
          ctl.change({ eventType: 'DELETE', old: { id } });
          return;
        }
        if (payload?.op === 'INSERT') deleted.delete(id);
        pending.add(id);
        if (!timer) timer = setTimeout(() => { void flush(); }, BOARD_HINT_WINDOW_MS);
      },
      onResync: () => { if (!dead) ctl.refetch(); },
      onUnavailable: () => { if (!dead) onUnavailable(); },
    });
    const onOnline = () => { if (!dead) ctl.refetch(); };
    if (typeof window !== 'undefined') window.addEventListener('online', onOnline);
    return () => {
      dead = true;
      if (timer) clearTimeout(timer);
      if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
      leave();
    };
  };
}

// ── The board's live rows (Umer, 2026-10-06) ───────────────────────────────
// Active cards, plus the cancelled cards and the archived cards of the last two Ottawa days (BOARD_DAYS)
// — the Cancelled and Archive columns. Older ones are History, read on demand. One server-side filter with
// its JavaScript mirror on realtime rows, so a card is on the board exactly while its own stamp is on the
// board's side of the cutoff (the board re-subscribes at Ottawa midnight) and Put back brings it straight in.
export function boardLiveFilter(cutoffIso: string) {
  const cut = Date.parse(cutoffIso);
  const since = (v: any) => v != null && Date.parse(v) >= cut;
  return {
    // One cache entry for the rule, not one per cutoff instant (the cutoff moves daily — every 30 s with
    // the testing window); the cached first paint is filtered through `accept`, so nothing stale shows.
    key: HISTORY_TEST_MINUTES > 0 ? `live:${HISTORY_TEST_MINUTES}m` : `live:${BOARD_DAYS}d`,
    or: `and(archived.eq.false,stage.neq.cancelled),and(archived.eq.false,cancelled_at.gte.${cutoffIso}),and(archived.eq.true,archived_at.gte.${cutoffIso})`,
    accept: (r: any) => (r?.archived ? since(r.archived_at) : r?.stage !== 'cancelled' || since(r.cancelled_at)),
    columns: ['archived', 'stage', 'cancelled_at', 'archived_at'],
  };
}

export function subscribeBoard(
  onCards: (cards: BoardCard[]) => void,
  onError?: (e: Error) => void,
  // Postgres only: a caller that reads a few fields per card (the Messages
  // page needs id / order number / customer name) names those columns and
  // gets thin rows — the full board row carries the emails_sent + activity
  // jsonb logs and weighed ~440 KB per read. `id` and the ordering column
  // always ride along; the other BoardCard fields come back as their empty
  // defaults. The Order Board and every other page keep full rows.
  // `liveV2: false` keeps a caller on the postgres_changes path even with
  // FEATURES.liveV2Board on (a role that may not join `team:board`).
  // `cutoffIso`: the History boundary the live rows are read against (boardHistory.historyCutoffIso).
  opts?: { columns?: string[]; activeOnly?: boolean; liveV2?: boolean; cutoffIso?: string },
): () => void {
  if (USE_SUPABASE) {
    const select = opts?.columns?.length
      ? [...new Set(['id', 'placed_at', ...opts.columns])].join(', ')
      : undefined;
    const base = {
      order: { column: 'placed_at', ascending: false as const },
      ...(select ? { select } : {}),
    };
    const legacy = (): (() => void) => {
      if (!opts?.activeOnly) return subscribeTable(TABLE, cardFromRow, onCards, { ...base, onError });
      // LIVE ROWS ONLY (0071). The Order Board asks for archived = false — a
      // server-side filter the subscription already mirrors on realtime rows, so
      // when the cron archives a card it leaves every open board on its own.
      // The board never downloads the archive, however many years it holds.
      // Since 2026-10-06 the live rows are boardLiveFilter: the active cards plus
      // the last two Ottawa days of cancelled and archived ones (the Cancelled and
      // Archive columns); older ones are History, read on demand.
      // Before 0113 (no cancelled_at) or 0071 (no archived) the filter cannot be
      // asked: fall back to archived = false, then to the whole board (paged),
      // rather than show nothing.
      const live = boardLiveFilter(opts.cutoffIso ?? historyCutoffIso());
      let unsub = subscribeTable(TABLE, cardFromRow, onCards, {
        ...base,
        where: live,
        onError: (e) => {
          if (!isSchemaGap(e)) { onError?.(e); return; }
          unsub();
          unsub = subscribeTable(TABLE, cardFromRow, onCards, {
            ...base,
            eq: [{ column: 'archived', value: false }],
            onError: (e2) => {
              if (!isSchemaGap(e2)) { onError?.(e2); return; }
              unsub();
              unsub = subscribeTable(TABLE, cardFromRow, onCards, { ...base, onError });
            },
          });
        },
      });
      return () => unsub();
    };
    if (!FEATURES.liveV2Board || opts?.liveV2 === false) return legacy();

    // Live v2: same cache-first paint, same first fetch, same filters — only
    // the source of changes differs. A refused join swaps in today's path.
    let dead = false;
    let unsub: () => void = () => {};
    const toLegacy = () => { if (dead) return; unsub(); unsub = legacy(); };
    const start = (activeOnly: boolean) => subscribeTable(TABLE, cardFromRow, onCards, {
      ...base,
      ...(activeOnly ? { where: boardLiveFilter(opts?.cutoffIso ?? historyCutoffIso()) } : {}),
      external: boardHintDriver(select || '*', toLegacy),
      onError: (e) => {
        if (dead) return;
        if (activeOnly && isSchemaGap(e)) { unsub(); unsub = start(false); return; }
        onError?.(e);
      },
    });
    unsub = start(!!opts?.activeOnly);
    return () => { dead = true; unsub(); };
  }
  return onSnapshot(
    query(collection(db, COL), orderBy('placedAt', 'desc')),
    (snap) => onCards(snap.docs.map((d) => ({ ...(d.data() as Omit<BoardCard, 'id'>), id: d.id }))),
    (e) => onError?.(e as Error)
  );
}

/** A request that never got an answer ("Failed to fetch" — a dropped connection, a gateway 5xx without
 *  CORS headers): worth another try. A refusal from the database is not. */
const isNetworkError = (e: { message?: string } | null | undefined): boolean =>
  /failed to fetch|networkerror|network request failed|load failed|fetch failed/i.test(String(e?.message || ''));
const NETWORK_RETRY_MS = [400, 1200];

export async function addCard(input: Omit<BoardCard, 'id' | 'createdAt'>): Promise<void> {
  if (USE_SUPABASE) {
    // Firestore used an auto-id here — generate one client-side. The same id is kept across retries,
    // so an insert that did land before its answer was lost is recognised, never made twice.
    const row = { id: crypto.randomUUID(), ...rowFromPatch(strip(input)), created_at: new Date().toISOString() };
    let { error } = await supabase().from(TABLE).insert(row);
    for (let i = 0; error && isNetworkError(error) && i < NETWORK_RETRY_MS.length; i++) {
      await new Promise((r) => setTimeout(r, NETWORK_RETRY_MS[i]));
      ({ error } = await supabase().from(TABLE).insert(row));
      if (error && (error.code === '23505' || /duplicate key/i.test(error.message || ''))) {
        // The earlier try may have landed: if this very row is there, it was saved.
        const { data } = await supabase().from(TABLE).select('id').eq('id', row.id).maybeSingle();
        if (data) return;
      }
    }
    if (error) {
      // 0071: one card per order is a database rule. Say which order, and what
      // to do instead, rather than surfacing a constraint name.
      if (error.code === '23505' || /order_key/.test(error.message || '')) {
        throw new Error(`A card for order #${input.orderNumber} already exists — open that one instead of creating another.`);
      }
      throw new Error(`${TABLE} insert: ${error.message}`);
    }
    return;
  }
  await addDoc(collection(db, COL), { ...strip(input), createdAt: Date.now() });
}

export async function updateCard(id: string, patch: Partial<Omit<BoardCard, 'id'>>): Promise<void> {
  if (USE_SUPABASE) {
    const row = rowFromPatch(strip(patch));
    if (!Object.keys(row).length) return; // PostgREST rejects an empty update; Firestore no-ops
    const { error } = await supabase().from(TABLE).update(row).eq('id', id);
    if (error) throw new Error(`${TABLE} update: ${error.message}`);
    return;
  }
  await updateDoc(doc(db, COL, id), strip(patch) as Record<string, unknown>);
}

// Supervisor override from Dispatch Oversight: set a card's owner regardless of
// who holds it now. An unassigned card moves to 'claimed' (same as the board's
// Claim button); a card already further along keeps its stage. A null owner
// returns it to the shared queue. This is why claiming on the board and
// reassigning from oversight stay one and the same — both write this card.
export async function reassignCard(
  card: Pick<BoardCard, 'id' | 'stage'> & Partial<Pick<BoardCard, 'orderType' | 'linkedOrderId'>>,
  owner: { id: string; name: string } | null
): Promise<void> {
  if (!owner) {
    await updateCard(card.id, { stage: 'unassigned', assignedTo: null, assignedToName: null, claimedAt: null });
    return;
  }
  // On the Same Day board Pending holds real orders only (Umer, 2026-09-25): a Same Day card that is not
  // an order yet takes its owner but stays in Unassigned — making the order moves it (Make order, 0098).
  const notOrderYet = FEATURES.orderFromBoard && card.orderType === 'delivery' && !card.linkedOrderId;
  const stage = card.stage === 'unassigned' && !notOrderYet ? 'claimed' : card.stage;
  await updateCard(card.id, { stage, assignedTo: owner.id, assignedToName: owner.name, claimedAt: Date.now() });
}

/** A change and its diary line in ONE write. Needed when a card that was claimed goes back to
 *  Unassigned: 0074's guard drops that move unless the same write adds to the diary (a separate
 *  append does not count). Reads the current diary first, like appendToJsonbArray. */
export async function updateCardWithActivity(id: string, patch: Partial<Omit<BoardCard, 'id'>>, entry: CardActivity): Promise<void> {
  if (!USE_SUPABASE) {
    await updateCard(id, patch);
    await logCardActivity(id, entry);
    return;
  }
  const { data, error } = await supabase().from(TABLE).select('activity').eq('id', id).single();
  if (error) throw new Error(`${TABLE} read activity: ${error.message}`);
  const cur = (data as any)?.activity;
  const activity = [...(Array.isArray(cur) ? cur : []), strip(entry)];
  const { error: upErr } = await supabase().from(TABLE).update({ ...rowFromPatch(strip(patch)), activity }).eq('id', id);
  if (upErr) throw new Error(`${TABLE} update: ${upErr.message}`);
}

// Append one entry to a card's jsonb array column (`emails_sent` / `activity`).
// Postgres has no arrayUnion — read the current array, append, write it back.
// The board has one owner per card, so concurrent appends are not a real risk.
async function appendToJsonbArray(id: string, column: 'emails_sent' | 'activity', entry: object): Promise<void> {
  const { data, error } = await supabase().from(TABLE).select(column).eq('id', id).single();
  if (error) throw new Error(`${TABLE} read ${column}: ${error.message}`);
  const cur = (data as any)?.[column];
  const next = [...(Array.isArray(cur) ? cur : []), entry];
  const { error: upErr } = await supabase().from(TABLE).update({ [column]: next }).eq('id', id);
  if (upErr) throw new Error(`${TABLE} append ${column}: ${upErr.message}`);
}

export async function recordEmailSent(id: string, entry: SentEmailLog): Promise<void> {
  if (USE_SUPABASE) {
    await appendToJsonbArray(id, 'emails_sent', entry);
    return;
  }
  await updateDoc(doc(db, COL, id), { emailsSent: arrayUnion(entry) });
}

// Append one diary entry. Best-effort — the diary must never block the action
// it describes (a claim that works but doesn't log still beats no claim).
export async function logCardActivity(id: string, entry: CardActivity): Promise<void> {
  try {
    if (USE_SUPABASE) {
      await appendToJsonbArray(id, 'activity', strip(entry));
      return;
    }
    await updateDoc(doc(db, COL, id), { activity: arrayUnion(strip(entry)) });
  } catch (e) {
    console.warn('logCardActivity failed (action still applied):', (e as Error).message);
  }
}

// Keep every email about ONE order in a single conversation. The first email a
// card sends becomes the thread root (its subject + Message-ID); each later one
// replies into it — same subject (Re:) + In-Reply-To/References to the prior
// IDs — so the customer sees one thread, not four separate messages. Returns
// the fresh subject unchanged when there's nothing to thread onto yet.
export function threadEmail(
  card: Pick<BoardCard, 'emailsSent' | 'orderNumber'>,
  freshSubject: string
): { subject: string; inReplyTo?: string; references?: string } {
  const sent = card.emailsSent || [];
  // The very first email about a card sets the thread root — send it as-is.
  if (!sent.length) return { subject: freshSubject };

  // Every LATER email rides ONE canonical subject so the customer sees a single
  // conversation. Keying off `sent.length` (not messageId count) is deliberate:
  // if an earlier send didn't record a Message-ID (older cards), the shipped/
  // final email used to fall back to its own subject and split off into a new
  // thread. It now always rides the root subject; references still chain
  // whenever Message-IDs exist so Gmail threads it too.
  const root =
    sent.find((s) => s.subject)?.subject ||
    (card.orderNumber ? `Your Uper Weed order #${card.orderNumber} — payment instructions` : freshSubject);
  const subject = /^re:\s/i.test(root) ? root : `Re: ${root}`;
  const ids = sent.map((s) => s.messageId).filter((id): id is string => !!id);
  return {
    subject,
    inReplyTo: ids.length ? ids[ids.length - 1] : undefined,
    references: ids.length ? ids.join(' ') : undefined,
  };
}

export async function removeCard(id: string): Promise<void> {
  if (USE_SUPABASE) {
    const { error } = await supabase().from(TABLE).delete().eq('id', id);
    if (error) throw new Error(`${TABLE} delete: ${error.message}`);
    return;
  }
  await deleteDoc(doc(db, COL, id));
}

// Link a board claim to the inbox. Claiming a card on the board only sets the
// CARD's owner; the inbox scopes an order thread by the EMAIL's owner. So when a
// dispatcher claims/releases a card, mark the matching order emails to the same
// owner — otherwise a claimed order keeps showing in other dispatchers' inbox.
// Matches every cached email whose parsed order number equals the card's, so the
// inbound order + any forward all move together. Best-effort: a failure here
// must never block the board action itself.
const normOrderNo = (s: string) => (s || '').replace(/\s+/g, '').toLowerCase();

// Does this cached email belong to this order? One matcher shared by the
// board↔inbox claim link and the card's Conversation panel, so they can never
// disagree about which emails an order owns.
export function matchesOrder(em: EmailRecord, orderNumber: string): boolean {
  const target = normOrderNo(orderNumber);
  return !!target && normOrderNo(extractOrderDetails(em).orderNumber || '') === target;
}

/**
 * Find a board card by its order number, for the Shipping screen's "fill from
 * order" lookup.
 *
 * Matching is forgiving on purpose: the number gets read off a card, an email
 * or a printed sheet, so a leading '#', stray spaces and casing must not decide
 * whether it is found. #ORD-20260818-FED079, ord-20260818-fed079 and
 * "ORD-20260818-FED079 " are the same order.
 *
 * Queries on the exact string first (indexed, one read) and only falls back to
 * scanning the board when that misses — the board is small, and a lookup that
 * quietly fails on a '#' is worse than one extra read.
 */
export async function findCardByOrderNumber(orderNumber: string): Promise<BoardCard | null> {
  const target = normOrderNo(String(orderNumber).trim().replace(/^#+/, ''));
  if (!target) return null;

  if (USE_SUPABASE) {
    // 0071: the normalised key is a unique indexed column — one read, any
    // spelling, any table size. (Everything below is the pre-0071 path.)
    try {
      const { data, error } = await supabase().from(TABLE).select('*').eq('order_key', orderKeyOf(orderNumber)).limit(1);
      if (error) throw new Error(`${TABLE}: ${error.message}`);
      return data?.length ? cardFromRow(data[0]) : null;
    } catch (e) {
      if (!isSchemaGap(e)) throw e;
    }
    const { data: exactRows, error } = await supabase()
      .from(TABLE)
      .select('*')
      .eq('order_number', String(orderNumber).trim())
      .limit(1);
    if (error) throw new Error(`${TABLE}: ${error.message}`);
    if (exactRows?.length) return cardFromRow(exactRows[0]);

    const allRows = await fetchAllRows<any>(() => supabase().from(TABLE).select('*').order('id'));
    for (const r of allRows || []) {
      const c = cardFromRow(r);
      if (normOrderNo(String(c.orderNumber || '').replace(/^#+/, '')) === target) return c;
    }
    return null;
  }

  const exact = await getDocs(
    query(collection(db, COL), where('orderNumber', '==', String(orderNumber).trim()))
  );
  if (!exact.empty) {
    const d = exact.docs[0];
    return { ...(d.data() as Omit<BoardCard, 'id'>), id: d.id };
  }

  const all = await getDocs(collection(db, COL));
  for (const d of all.docs) {
    const c = { ...(d.data() as Omit<BoardCard, 'id'>), id: d.id };
    if (normOrderNo(String(c.orderNumber || '').replace(/^#+/, '')) === target) return c;
  }
  return null;
}

export async function claimOrderEmails(
  orderNumber: string,
  owner: { id: string; name: string } | null
): Promise<void> {
  try {
    if (!normOrderNo(orderNumber)) return;
    const cached = await loadCachedEmails();
    const docs = cached
      .filter((e) => matchesOrder(e, orderNumber))
      .map((e) => ({ folder: e.folder, uid: e.uid }));
    if (!docs.length) return;
    if (owner) await reassignThread(docs, owner);
    else await releaseThread(docs);
  } catch (e) {
    console.warn('claimOrderEmails failed (board claim still applied):', (e as Error).message);
  }
}

// The reverse link: claiming/releasing an order's conversation in the INBOX also
// moves its board card to the same owner, so the board mirrors the inbox. No-op
// if the card doesn't exist yet (it'll be created Unassigned by the mail sync).
// Best-effort — never blocks the inbox claim.
export async function assignCardByOrder(
  orderNumber: string,
  owner: { id: string; name: string } | null
): Promise<void> {
  try {
    if (!normOrderNo(orderNumber)) return;
    if (USE_SUPABASE) {
      const { data, error } = await supabase()
        .from(TABLE)
        .select('id, stage, order_type, linked_order_id')
        .eq('order_number', orderNumber);
      if (error) throw new Error(`${TABLE}: ${error.message}`);
      for (const r of (data || []) as any[]) {
        await reassignCard({
          id: r.id,
          stage: r.stage as PaymentStage,
          ...(r.order_type ? { orderType: r.order_type } : {}),
          ...(r.linked_order_id ? { linkedOrderId: r.linked_order_id } : {}),
        }, owner);
      }
      return;
    }
    const snap = await getDocs(query(collection(db, COL), where('orderNumber', '==', orderNumber)));
    for (const d of snap.docs) {
      const card = d.data() as BoardCard;
      await reassignCard({ id: d.id, stage: card.stage }, owner);
    }
  } catch (e) {
    console.warn('assignCardByOrder failed (inbox claim still applied):', (e as Error).message);
  }
}

// Firestore rejects undefined values — drop them (null is fine and used to
// clear fields like paidAt when a card moves back out of "completed").
function strip<T extends object>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;
}

// ---------------------------------------------------------------------------
// Auto-created cards — parse outgoing order-confirmation emails.
//
// When staff email a customer their order confirmation (the template format:
// "Hi <name>", an "Order:"/order number, an "Items:" line, a "Total:" line and
// a payment method — Interac e-Transfer or card), the Sent-folder sync feeds
// those messages through here and a Pending card appears on the board without
// anyone re-typing the order.
// ---------------------------------------------------------------------------

export interface ParsedOrderEmail {
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  phone: string;
  address: string;
  total: number;
  itemsSummary: string;
  city: string;
  distanceKm: number;
  paymentMethod: PaymentMethod;
  orderType?: 'delivery' | 'shipping';
}

// Lenient field extraction — returns whatever it can find. Used directly by
// the Email Inbox "Email customer" prefill (works on the AI's inbound order
// emails too), and by parseOrderEmail below with strictness on top.
export interface ExtractedOrderDetails {
  orderNumber?: string;
  customerName?: string;
  customerEmail?: string; // an address found in the body (AI emails often include it)
  phone?: string;
  address?: string; // full delivery address line (website order emails)
  total?: number;
  itemsSummary?: string;
  city?: string;
  distanceKm?: number;
  paymentMethod?: PaymentMethod;
  orderType?: 'delivery' | 'shipping'; // mail order vs same-day local run
}

// Same-day vs mail order — LOCATION is the only test (Umer, 2026-09-19).
// SAME-DAY = the Ottawa / Gatineau area the website serves with its own
// drivers. MAIL = everywhere else, the rest of Canada and the USA. Payment
// method plays no part (cash was dropped from the rule on 2026-09-19).
//
// In order:
//   1. the postal code — the checkout's own local tier, verified against every
//      real order since Sep 4: K1*, K2*, K4A, J8*, and the Gatineau/Aylmer part
//      of J9 (J9A, J9H, J9J). Any other Canadian postal code is mail: Val-d'Or
//      J9P, Kingston K7L, Arnprior K7S, Cornwall PE C0A …;
//   2. a US address (ZIP code or state) is mail — before any city word, so
//      "Gloucester Point, VA" never matches "gloucester";
//   3. the city field, normalised (lower-case, accents stripped, punctuation
//      out) and matched WORD by word against the website's Local Delivery
//      list — "Gatineau aylmer", "GATINEAU", "Orléans" all count;
//   4. the city field said nothing useful ("Ontario", "Gat", "QC") → the same
//      word match over the whole address line;
//   5. nothing found → mail, and the popup flags it for a human.
// 2026-09-29 (client: "some local orders are not coming to the Same Day board"): step 1 sent every
// listed town OUTSIDE the core postal codes to Mail — Rockland K4K, Embrun / Casselman / Carp / Almonte
// K0A, Kemptville K0G, Perth K7H, Cornwall K6H-K, Brockville K6V, Navan K4B. An Ontario (K) or Québec (J)
// postal code outside the core list now counts as same day when the city is one of those listed towns
// (OUTLYING_SAME_DAY_TOWNS). The core cities never override a postal code (a K7L "Ottawa" stays mail),
// and a postal code from another province never does (Cornwall PE, C0A, stays mail).
export const SAME_DAY_FSA = /^(?:K1[A-Z]|K2[A-Z]|K4A|J8[A-Z]|J9[ABHJ]|J0X)$/; // J9B Chelsea, J0X Thurso/Luskville (Umer, 2026-09-24)
export const SAME_DAY_CITIES = [
  // Ottawa & Gatineau & surrounding areas (uperweedca.com/locations)
  'ottawa', 'nepean', 'kanata', 'barrhaven', 'orleans', 'gloucester', 'vanier',
  'stittsville', 'carp', 'navan', 'rockland', 'embrun', 'casselman', 'kemptville',
  'perth', 'almonte', 'cornwall', 'brockville',
  'gatineau', 'hull', 'aylmer',
  // Outaouais towns added 2026-09-24 (Umer): Cantley, Chelsea, Buckingham,
  // Thurso, Luskville, Angers. Val-des-Monts is same-day through its J8N
  // postal code (the list holds single words).
  'cantley', 'chelsea', 'buckingham', 'thurso', 'luskville', 'angers',
];
// The listed towns whose postal codes are outside SAME_DAY_FSA (see the 2026-09-29 note above).
export const OUTLYING_SAME_DAY_TOWNS = ['rockland', 'embrun', 'casselman', 'carp', 'almonte', 'kemptville', 'perth', 'cornwall', 'brockville', 'navan'];
const US_STATE =
  /,\s*(?:AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY|DC)\s*(?:,|$)/;
const US_ZIP = /(?:^|[\s,])\d{5}(?:-\d{4})?(?=[\s,]|$)/;
export const isUsAddress = (address: string): boolean =>
  US_STATE.test(address) || US_ZIP.test(address) || /\b(?:usa|united states)\b/i.test(address);

const normWords = (text: string): string[] =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
export const mentionsSameDayCity = (text: string | undefined): boolean =>
  !!text && normWords(text).some((w) => SAME_DAY_CITIES.includes(w));

export function detectOrderType(
  city?: string,
  postal?: string,
  address?: string
): 'delivery' | 'shipping' {
  const fsa = String(postal || '').replace(/\s+/g, '').toUpperCase().slice(0, 3);
  if (/^[A-Z]\d[A-Z]$/.test(fsa)) {
    if (SAME_DAY_FSA.test(fsa)) return 'delivery';
    // An Ontario / Québec postal code outside the core: a listed outlying town is still same day.
    const town = city || cityFromAddress(address || '') || '';
    if (/^[KJ]/.test(fsa) && normWords(town).some((w) => OUTLYING_SAME_DAY_TOWNS.includes(w))) return 'delivery';
    return 'shipping';
  }
  const addr = address || '';
  if (isUsAddress(addr) || isUsAddress(city || '')) return 'shipping';
  if (mentionsSameDayCity(city)) return 'delivery';
  if (mentionsSameDayCity(addr)) return 'delivery';
  return 'shipping';
}

/** The words that mean the crypto checkout: the website's own label, the processor, the coins. */
export const CRYPTO_WORDS = /\bcrypto(?:currency)?\b|payer\s*url|\bbitcoin\b|\bbtc\b|\busdt\b|\busdc\b|\beth(?:ereum)?\b/i;

/** True when the mail carries the website's own "Payment Method" field with a known value — the one
 *  source allowed to correct an existing card's method (the loose sweep is not). */
export function paymentMethodFromField(em: Pick<EmailRecord, 'subject' | 'bodyText'>): boolean {
  const hay = `${em.subject || ''}\n${em.bodyText || ''}`;
  const pmField = hay.match(/payment\s*method\s*:?\s*\**\s*([^\n]{0,40})/i)?.[1] || '';
  return /cash|e-?transfer|interac|card/i.test(pmField) || CRYPTO_WORDS.test(pmField);
}

export function extractOrderDetails(em: EmailRecord): ExtractedOrderDetails {
  // Strip reply quote markers (">", ">>") so an order QUOTED inside a reply is
  // parsed the same as a fresh one — that's how an older card recovers its
  // address/details from the customer's later replies.
  const text = (em.bodyText || '')
    .split('\n')
    .map((l) => l.replace(/^\s*(?:>\s*)+/, ''))
    .join('\n');
  const hay = `${em.subject || ''}\n${text}`;
  const out: ExtractedOrderDetails = {};

  // The website's own "Payment Method" field decides first — its label is
  // glued to the value in the text body ("Payment MethodCash on Delivery"), so
  // a word-boundary match on "cash" alone misses it. The loose sweep below is
  // only for emails that carry no such field (AI receptionist, replies).
  const pmField = hay.match(/payment\s*method\s*:?\s*\**\s*([^\n]{0,40})/i)?.[1] || '';
  if (/cash/i.test(pmField)) out.paymentMethod = 'cash';
  else if (CRYPTO_WORDS.test(pmField)) out.paymentMethod = 'crypto'; // "Crypto (PayerURL)"
  else if (/e-?transfer|interac/i.test(pmField)) out.paymentMethod = 'etransfer';
  else if (/card/i.test(pmField)) out.paymentMethod = 'card';
  else if (/e-?transfer|interac/i.test(hay)) out.paymentMethod = 'etransfer';
  else if (/cash\s*on\s*delivery|\bcash\b/i.test(hay)) out.paymentMethod = 'cash';
  else if (CRYPTO_WORDS.test(hay)) out.paymentMethod = 'crypto';
  else if (/\bcard\b/i.test(hay)) out.paymentMethod = 'card';

  // Real order numbers always contain a digit (ORD-TEST-001, 20260807-005) —
  // the lookahead stops plain words like "#confirmation" (from a template
  // subject) being mistaken for one and poisoning the next prefill.
  const num = hay.match(/order[:\s#]*(?=[A-Z0-9-]*\d)([A-Z0-9][A-Z0-9-]{4,})/i)?.[1];
  if (num) out.orderNumber = num;

  // Total, most-specific first: GRAND TOTAL (AI emails), then the website's
  // "Order Total" (value on the next line, always $-prefixed), then a bare
  // "total" ONLY when a colon or $ sign follows. The bare form must never
  // accept a naked digit run: the website's items-table header ends with
  // "Total" right before the first item name, and an item like
  // "4Oz for $340 Premium…" once turned a $340 order into a $4 card.
  // "Subtotal" stays excluded — the char before "total" can't be "sub"'s b.
  const totalStr =
    text.match(/grand\s*total\s*:?\s*\$?\s*([\d,]+(?:\.\d{1,2})?)/i)?.[1] ||
    text.match(/order\s*total[^\d$]{0,60}\$\s*([\d,]+(?:\.\d{1,2})?)/i)?.[1] ||
    text.match(/(?:^|[^b])total(?:\s*paid|\s*due)?\s*:\s*\$?\s*([\d,]+(?:\.\d{1,2})?)/i)?.[1] ||
    text.match(/(?:^|[^b])total(?:\s*paid|\s*due)?\s*\$\s*([\d,]+(?:\.\d{1,2})?)/i)?.[1] ||
    '';
  const total = parseFloat(totalStr.replace(/,/g, ''));
  if (total > 0) out.total = total;

  // Bracketed placeholders ("[CUSTOMER NAME]", "$[TOTAL]") come from template
  // emails whose gaps were never filled — never let them become card data.
  const isPlaceholder = (s: string) => /\[[^\]]*\]/.test(s);

  // The live website's admin-copy order email flattens its HTML tables into
  // text with the labels GLUED to the values ("NameHank Savard",
  // "Emailhanksavard@hotmail.com", "Phone705 920 3728") — so these label
  // patterns anchor to the line start and take zero-or-more separator chars.
  // They run FIRST: labels beat greetings and generic sweeps.
  // A greeting name is only trustworthy when WE greet the customer ("Hello
  // Ayush"). An inbound "Hello team / there / all" greets US, not a customer —
  // those generic targets must never become the customer's name (the "team" bug).
  const greetRaw = text.match(/^\s*(?:hi|hello|dear)\s+([^,\n]{1,40})[,\n]/im)?.[1]?.trim();
  const greet =
    greetRaw && !/^(team|there|all|everyone|folks|guys|sir|madam|support|customer|friend|hi|hello|uper\s*weed)$/i.test(greetRaw)
      ? greetRaw
      : '';
  const name = (
    text.match(/new order received from\s+([^\n]{2,60})/i)?.[1] ||
    text.match(/^\s*Name\s*:?\s*(\S[^\n@]{1,59})\s*$/m)?.[1] ||
    greet ||
    text.match(/customer(?:\s*name)?\s*:\s*(.+)/i)?.[1] ||
    ''
  ).trim();
  if (name && !isPlaceholder(name)) out.customerName = name;

  const phone = text.match(/^\s*Phone\s*:?\s*([+()\d][\d\s().-]{5,20})/im)?.[1]?.trim();
  if (phone) out.phone = phone;

  // Website format: "Delivery Address" heading, full address on the next line
  // ("299 brebeuf ave. Apt #1, Sudbury, ON, P3C 5H2") — city is the part
  // before the two-letter province. City is accent-aware so Québec cities
  // (Montréal, Trois-Rivières, l'Île-Perrot…) parse, not just ASCII ones.
  const cityFrom = cityFromAddress;
  // The label may be GLUED to the value in the flattened HTML ("Delivery
  // Address2192 Avenue…") or separated by newlines — `\s*:?\s*` handles both.
  const address = text.match(/delivery address\s*:?\s*([^\n\r]{5,120})/i)?.[1]?.trim();
  if (address && !isPlaceholder(address)) {
    out.address = address;
    const addrCity = cityFrom(address);
    if (addrCity) out.city = addrCity;
  }
  // Fallback: any line carrying a Canadian postal code is almost certainly the
  // delivery address — catches formats the "Delivery Address" label misses
  // (French emails, reworded templates, glued labels). Postal shape L#L #L# is
  // distinctive, so order numbers / phones don't false-match; skip our own
  // e-transfer lines, and strip a leading "Delivery Address" label if glued on.
  if (!out.address) {
    const postalLine = text
      .split('\n')
      .map((l) => l.trim())
      .find(
        (l) =>
          /[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d/.test(l) &&
          l.length >= 8 &&
          l.length <= 140 &&
          !/uperweed|protonmail|recipient|e-?transfer|security/i.test(l)
      );
    if (postalLine && !isPlaceholder(postalLine)) {
      const cleaned = postalLine.replace(/^delivery address\s*:?\s*/i, '').trim();
      out.address = cleaned;
      const c = cityFrom(cleaned);
      if (c) out.city = c;
    }
  }

  const items = text.match(/items?\s*:\s*(.+)/i)?.[1]?.trim();
  if (items && !isPlaceholder(items)) out.itemsSummary = items;

  // Website "Order Items" table. The HTML flattens each row in ONE OF TWO ways
  // (both seen in real mail): every cell on its own line (name / variant / qty /
  // $price / $line-total), OR the row tail on a single tab-glued line
  // ("Oz ( 28 grams )   2   $150.00   $300.00"). The state machine must close a
  // row on EITHER form — treating a combined line as variant text used to
  // swallow its qty, and the NEXT row's stray number then closed this item with
  // the wrong quantity (the "email says 2×, card says 1×" bug).
  if (!out.itemsSummary) {
    const section = text.match(/order items([\s\S]*?)(?:\n\s*subtotal\b|$)/i)?.[1];
    if (section) {
      const lines = section
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        // Header cells — separate ("Item"/"Qty"/…) or glued onto one line.
        .filter((l) => !/^(item|qty|price|total)$/i.test(l) && !/^item\s+qty\s+price\s+total$/i.test(l));
      const rows: string[] = [];
      let itemName = '';
      let variant = '';
      const flush = (qty: string) => {
        if (itemName) rows.push(`${qty}× ${itemName}${variant ? ` (${variant})` : ''}`);
        itemName = '';
        variant = '';
      };
      for (let i = 0; i < lines.length; i++) {
        const l = lines[i];
        // Combined row tail: "<variant?>  <qty>  $price  $line-total".
        const tail = l.match(/^(.*?)\s*\b(\d{1,3})\s+\$[\d,.]+\s+\$[\d,.]+$/);
        if (tail) {
          const lead = tail[1].trim();
          if (lead) {
            if (!itemName) itemName = lead;
            else variant = variant ? `${variant} ${lead}` : lead;
          }
          flush(tail[2]);
          continue;
        }
        if (/^\d{1,3}$/.test(l)) {
          // A bare integer is the QTY only when the price cell follows it — a
          // VARIANT can literally be the text "1" ("1200MG THC Edibles" comes in
          // variant "1"), and reading that as the qty gave 1× on a 2× order.
          const next = lines[i + 1] || '';
          if (/^\$[\d,.]+/.test(next)) {
            flush(l);
          } else if (!itemName) itemName = l;
          else variant = variant ? `${variant} ${l}` : l;
          continue;
        }
        if (/^\$[\d,.]+(?:\s+\$[\d,.]+)*$/.test(l)) {
          // price cells — incl. sale rows where regular+sale price share a line
          // ("$180.00 $150.00"); those must never become an item name.
          continue;
        }
        if (!itemName) itemName = l;
        else variant = variant ? `${variant} ${l}` : l;
      }
      if (rows.length) out.itemsSummary = rows.join('; ');
    }
  }

  // Fallback: the email's "Copy / Paste Summary" lists items as "<qty> X <name>"
  // lines ("2 X 1200MG THC Edibles"). Used when the table section is missing or
  // yields nothing — qty is explicit per line, so it can't be misassociated.
  if (!out.itemsSummary) {
    const nx = text
      .split('\n')
      .map((l) => l.trim())
      // Name capture is generous ({1,200}) — long bundle/craft names are real,
      // and a non-matching line silently DROPS the item. Keep any trailing
      // digits: they're usually a dosage/variant discriminator ("Gummies 300",
      // variant "1"), and destroying information is worse than a stray token.
      .map((l) => l.match(/^(\d{1,3})\s*[xX×]\s+(\S.{1,200})$/))
      .filter((m): m is RegExpMatchArray => !!m)
      .map((m) => `${m[1]}× ${m[2].trim()}`);
    if (nx.length) out.itemsSummary = nx.join('; ');
  }

  if (!out.city) {
    const city = (
      text.match(/ship(?:ping)?\s+to\s+([^(\n]+?)\s*(?:\(|\n|$)/i)?.[1] ||
      text.match(/^\s*city\s*:\s*(.+)/im)?.[1] ||
      ''
    ).trim();
    if (city) out.city = city;
  }

  const km = parseInt((text.match(/\(([\d,]+)\s*km/i)?.[1] || '').replace(/,/g, ''), 10);
  if (Number.isFinite(km)) out.distanceKm = km;

  const postal = (out.address || '').match(/[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d/)?.[0];
  out.orderType = detectOrderType(out.city, postal, out.address);

  // Email address: the label-anchored form first — on glued lines
  // ("Emailhanksavard@hotmail.com") the label match correctly captures just
  // the address, where the generic sweep below would swallow the label into
  // the address and produce a wrong-but-valid "Emailhanksavard@…".
  // The website's offline order email (uperweed.com, 2026-09-28) puts that
  // label on the NAME's line — "NameJane Doe Emailjane@x.com [jane@x.com]" —
  // so the second pattern finds it after the name. It is tied to a line that
  // starts with the Name label: a bare "email…@" elsewhere in a body is an
  // address that really begins that way, and stays whole.
  const labeledEmail = (
    text.match(/^\s*E-?mail\s*:?\s*([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/im) ||
    text.match(/^\s*Name\s*:?[^\n@]*?\sE-?mail\s*:?\s*([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/im)
  )?.[1];
  if (labeledEmail && !/uperweed/i.test(labeledEmail)) {
    out.customerEmail = labeledEmail;
  } else {
    // Any address mentioned in the body that isn't one of ours is very likely
    // the customer's (AI order emails include it). Quoted/forwarded header
    // lines ("From: Wadood <…>", "> …") name OTHER people — skip them so a
    // teammate's address from an old chain can't become the customer's.
    const bodyEmail = text
      .split('\n')
      .filter((l) => !/^\s*(>|from:|to:|cc:|reply-to:)/i.test(l))
      .join('\n')
      .match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/)?.[0];
    if (bodyEmail && !/uperweed/i.test(bodyEmail)) out.customerEmail = bodyEmail;
  }

  return out;
}

// ---------------------------------------------------------------------------
// Quoted-thread splitting — one inbound email usually CONTAINS the whole
// back-and-forth as quoted history ("On … wrote:" chains, "-------- Original
// message --------" blocks). The card's Conversation panel splits that into
// separate bubbles so a 2-way conversation reads as one, with each side
// attributed. Pure text processing — no Firestore.
// ---------------------------------------------------------------------------

export interface ConvoBubble {
  from: 'us' | 'customer' | 'unknown';
  text: string;
}

// Which side of the conversation an address belongs to. Covers the business
// domain(s) and the internal test mailboxes used on staging.
const OUR_SIDE = /uperweed|umerakram|umer\.claude/i;

// Returns segments NEWEST-FIRST: [own new text, most recent quote, …, oldest
// quote] — callers reverse for chronological display. `topFrom` is who wrote
// the email itself (by folder); `subjectHint` helps cut content out of
// glued "Original message" headers (the quoted subject is the thread's own).
export function splitQuotedThread(
  body: string,
  topFrom: 'us' | 'customer',
  subjectHint?: string
): ConvoBubble[] {
  // Strip reply arrows ("> ", ">> ") — authorship comes from the markers.
  const flat = String(body || '')
    .split('\n')
    .map((l) => l.replace(/^\s*(?:>\s*)+/, ''))
    .join('\n');

  const markers: { start: number; end: number; addr: string; orig: boolean }[] = [];
  // Three quote styles, all seen in real customer mail:
  //   1. "----- Original message -----  From: Name <addr>"          (orig)
  //   2. Outlook/Samsung "____________  From: Name <addr>  Sent: …  Subject: …"
  //      (orig — a header block; the quoted body starts after Subject:)
  //   3. Gmail "On <date> Name <addr> wrote:"
  // Groups: 1 = original-msg addr, 2 = Outlook addr, 3 = "On … wrote" addr.
  const re =
    /-{4,}\s*Original message\s*-{4,}\s*From:\s*[^\n<]{0,80}<([^>\s]+@[^>\s]+)>|_{6,}\s*From:\s*[^\n<]{0,80}<([^>\s]+@[^>\s]+)>|On [^\n]{5,140}?<?([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})>?[^\n]{0,120}?wrote:/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(flat))) {
    markers.push({
      start: m.index,
      end: m.index + m[0].length,
      addr: m[1] || m[2] || m[3] || '',
      // groups 1 & 2 are header blocks (From/Sent/To/Subject) — cut the body
      // after the quoted Subject line; group 3 ("On … wrote:") has none.
      orig: !!m[1] || !!m[2],
    });
  }

  const root = (subjectHint || '').replace(/^(\s*(re|fwd?):\s*)+/i, '').trim();
  const segments: ConvoBubble[] = [];
  const head = flat.slice(0, markers.length ? markers[0].start : flat.length).trim();
  if (head) segments.push({ from: topFrom, text: head });

  for (let i = 0; i < markers.length; i++) {
    const mk = markers[i];
    let bodyStart = mk.end;
    if (mk.orig) {
      // "Original message" style crams From/Date/To/Subject and the content
      // into one run — the content starts right after the quoted subject,
      // which is this thread's own subject. Cut there when findable.
      const window = flat.slice(mk.end, mk.end + 400);
      const sIdx = window.search(/Subject:/i);
      if (sIdx >= 0) {
        let cut = sIdx + 'Subject:'.length;
        if (root) {
          const rIdx = window.toLowerCase().indexOf(root.toLowerCase(), cut);
          if (rIdx >= 0 && rIdx < 350) cut = rIdx + root.length;
        }
        bodyStart = mk.end + cut;
      }
    }
    const bodyEnd = i + 1 < markers.length ? markers[i + 1].start : flat.length;
    const text = flat.slice(bodyStart, bodyEnd).trim();
    if (!text) continue;
    const from = mk.addr ? (OUR_SIDE.test(mk.addr) ? 'us' : 'customer') : 'unknown';
    segments.push({ from, text });
  }
  return segments;
}

export function parseOrderEmail(em: EmailRecord): ParsedOrderEmail | null {
  const d = extractOrderDetails(em);
  // An order needs an order number and a dollar total — the same signal the
  // inbox uses to tag a thread ORDER, so anything that reads as an order in the
  // inbox also becomes a board card. Payment method is best-effort: the board is
  // the e-Transfer department (AI orders are e-transfer only), so default there
  // when the email doesn't spell it out.
  if (!d.orderNumber || !(d.total && d.total > 0)) return null;
  const paymentMethod: PaymentMethod = d.paymentMethod || 'etransfer';

  // Who the customer is depends on direction. On a SENT confirmation the
  // recipient (To) is the customer. On an INBOUND order — a customer emailing
  // us, or the AI receptionist forwarding one — the customer is the SENDER,
  // or, for AI mail sent from one of our own addresses, the address in the body.
  const isUper = (a: string) => /uperweed/i.test(a);
  const toAddr = ((em.to || '').match(/<([^>]+)>/)?.[1] || (em.to || '').split(',')[0] || '').trim();
  const fromAddr = ((em.from || '').match(/<([^>]+)>/)?.[1] || em.from || '').trim();

  const customerEmail =
    em.folder === 'sent'
      ? toAddr || d.customerEmail || ''
      : d.customerEmail || (fromAddr && !isUper(fromAddr) ? fromAddr : '') || '';
  const customerName =
    d.customerName ||
    (em.folder !== 'sent' && em.fromName && !isUper(em.from || '') ? em.fromName : '') ||
    customerEmail;

  return {
    orderNumber: d.orderNumber,
    customerName,
    customerEmail,
    phone: d.phone || '',
    address: d.address || '',
    total: d.total,
    itemsSummary: d.itemsSummary || '',
    city: d.city || '',
    distanceKm: d.distanceKm || 0,
    paymentMethod,
    orderType: d.orderType,
  };
}

// The board's gatekeeper: is this a GENUINE new-order email — the only kind
// that should ever mint a card? An order number + a total appears in far more
// mail than actual orders: customer replies, support questions, shipping and
// payment notices all QUOTE the order in their history, and the old
// "has an order number + a total" test wrongly treated every one of them as an
// order. This recognizes the real thing by its shape, not by stray numbers:
//   • the website storefront's admin-copy order notification,
//   • the AI receptionist's order email,
//   • our own first order-arrival / payment-instructions send.
// A reply or forward (subject starts Re:/Fwd:) is a conversation ABOUT an
// order, never a new one — rejected outright.
// The non-quoted TOP of an email — everything before the first quoted-reply
// marker ("On … wrote:", "----- Original message -----"). New-order detection
// must read THIS, not the quoted history below it: a customer's simple question
// that merely QUOTES their order confirmation must never be mistaken for a new
// order and minted as an Unassigned card.
export function topOfEmail(body: string): string {
  const flat = String(body || '')
    .split('\n')
    .map((l) => l.replace(/^\s*(?:>\s*)+/, ''))
    .join('\n');
  const re =
    /-{4,}\s*Original message\s*-{4,}|On [^\n]{5,140}?<?[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}>?[^\n]{0,120}?wrote:/i;
  const m = flat.match(re);
  return m && m.index != null ? flat.slice(0, m.index) : flat;
}

export function isNewOrderEmail(em: EmailRecord): boolean {
  const subject = em.subject || '';
  // Only the email's OWN content — quoted order history below a reply/question
  // must not qualify it as a new order.
  const body = topOfEmail(em.bodyText || '');

  if (/^\s*(re|fwd?|fw)\s*:/i.test(subject)) return false;

  // Only an INBOUND order ARRIVAL mints a card (per the owner, 2026-08-18). The
  // website's admin copy and the AI receptionist's order both land in our inbox.
  // Our own SENT mail — payment instructions, payment confirmations, Canada-Post
  // / postal-code notices, complaint replies — are all follow-ups on an EXISTING
  // order and must NEVER create a card. Otherwise an old, already-handled order
  // resurfaces as a stale, detail-less card built from reply mail, and its
  // "conversation" is just our follow-ups instead of the real order thread.
  if (em.folder === 'sent') return false;

  // Website storefront (info@ receives the ADMIN COPY of each new order).
  if (/new order received from/i.test(body)) return true;
  if (/admin copy\b[\s\S]{0,40}origin website/i.test(body)) return true;
  // Website subject: "[uperweedca.com] New Order ORD-… — Name".
  if (/\bnew order\b/i.test(subject) && /\bORD[-\s]?\d/i.test(subject)) return true;

  // AI receptionist — its order email leads with "AI RECEPTIONIST — new order".
  if (/ai receptionist\s*[—\-]\s*new order/i.test(body)) return true;
  if (/receptionist/i.test(em.fromName || '') && /\bnew order\b/i.test(body)) return true;

  return false;
}

// Turn genuine new-order emails into Unassigned cards for any order the board
// doesn't have yet (deduped by order number; the deterministic doc id keeps two
// admins syncing at once from double-creating). Only isNewOrderEmail() mail
// gets through — order-related replies/support/shipping never mint a card. They
// sit unowned until someone claims them.
export async function autoCreateCardsFromEmails(emails: EmailRecord[]): Promise<BoardCard[]> {
  // A site that hides the Order Board must not WRITE to it (Umer, 2026-09-16).
  // Production runs with main parity on, so nobody there can open the board —
  // but the Email sync called this on every fetched message regardless, and a
  // backlog drain quietly minted cards into the live database that no one
  // could see or clear. Card creation belongs to the board: same switch.
  if (!FEATURES.orderBoard) return [];
  const parsed = emails
    .map((em) => ({ em, p: parseOrderEmail(em) }))
    .filter(
      (x): x is { em: EmailRecord; p: ParsedOrderEmail } => !!x.p && isNewOrderEmail(x.em)
    );
  if (!parsed.length) return [];

  // Canonical order-number key — uppercase, alphanumerics only — so the SAME
  // order can never mint two cards just because two email copies spelled its
  // number differently ("ORD-20260818-A", "ord 20260818 a"). Used for dedup AND
  // the doc id, so both agree.
  const normKey = (n: string) => (n || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

  // One read of every existing card instead of a query per email — so
  // re-scanning the whole cache to backfill missing cards stays cheap and
  // never resurrects a claimed card as a fresh Unassigned one.
  const existing = new Map<string, { id: string; card: BoardCard }>();
  const dupIds: string[] = [];
  // Which unique key the create's ON CONFLICT names. 'order_key' once 0071 is
  // applied (one card per order, whatever the id); the primary key before.
  let createConflict: 'order_key' | 'id' = 'order_key';
  if (USE_SUPABASE) {
    // ASK ABOUT THIS BATCH, NOT THE WHOLE BOARD (2026-09-21). The old read of
    // every card was the sync's only defence against re-creating a live one,
    // and it was silently cut at the API's 1000-row cap — with the board at
    // 564 and growing ~25 a day, the newest cards (the ones being worked)
    // would have dropped off its end within weeks and been re-created on
    // every sync. A lookup by key is one indexed read of at most a batch,
    // whatever the table holds. Duplicate deletion is gone with it: since
    // 0071 the database refuses a second card for an order.
    const keys = [...new Set(parsed.map((x) => normKey(x.p.orderNumber)).filter(Boolean))];
    let rows: any[];
    try {
      rows = keys.length
        ? await fetchAllRows<any>(() => supabase().from(TABLE).select('*').in('order_key', keys).order('id'))
        : [];
    } catch (e) {
      if (!isSchemaGap(e)) throw e;
      // 0071 not applied here yet: the whole-board read, PAGED so it cannot
      // truncate, and creates guard on the id instead.
      createConflict = 'id';
      rows = await fetchAllRows<any>(() => supabase().from(TABLE).select('*').order('id'));
    }
    for (const r of rows) {
      const card = cardFromRow(r);
      if (!card.orderNumber) continue;
      const key = normKey(card.orderNumber);
      if (!existing.has(key)) existing.set(key, { id: card.id, card });
    }
  } else {
  const snap = await getDocs(collection(db, COL));
  snap.forEach((d) => {
    const card = d.data() as BoardCard;
    if (!card.orderNumber) return;
    const key = normKey(card.orderNumber);
    const prior = existing.get(key);
    if (!prior) { existing.set(key, { id: d.id, card }); return; }
    // Two cards for the SAME order already exist (numbers differed only in case/
    // format before this normalization). Drop the redundant UNCLAIMED email-sync
    // copy — never a claimed or hand-created card — keeping whichever has more
    // parsed data.
    const unclaimed = isUntouchedSyncCard;
    const score = (c: BoardCard) => (c.address ? 1 : 0) + (c.itemsSummary ? 1 : 0) + (c.phone ? 1 : 0) + (c.city ? 1 : 0);
    if (unclaimed(prior.card) && unclaimed(card)) {
      if (score(card) > score(prior.card)) { dupIds.push(prior.id); existing.set(key, { id: d.id, card }); }
      else { dupIds.push(d.id); }
    }
  });
  }
  for (const dupId of dupIds) {
    try { await removeCard(dupId); } catch { /* best-effort — next sync retries */ }
  }

  const created: BoardCard[] = [];
  const repaired = new Set<string>();
  for (const { em, p } of parsed) {
    const key = normKey(p.orderNumber);
    const prior = existing.get(key);
    if (prior) {
      // Repair pass: earlier parser versions produced junk fields on
      // auto-created cards (glued "Email…" names, a $4 total from a $340
      // order, empty items/phone/address) — and creation dedup means they
      // never self-corrected. A card email-sync created that nobody has
      // claimed yet was never touched by a human, so a fresh parse may fix
      // it in place. Best-effort; claimed/worked cards are never rewritten.
      if (isUntouchedSyncCard(prior.card) && !repaired.has(key)) {
        repaired.add(key);
        const cur = prior.card;
        const patch: Partial<BoardCard> = {};
        const badName = !cur.customerName || /@/.test(cur.customerName) || /^email/i.test(cur.customerName);
        if (p.customerName && !/@/.test(p.customerName) && badName && p.customerName !== cur.customerName) {
          patch.customerName = p.customerName;
        }
        if (p.customerEmail && p.customerEmail !== cur.customerEmail && (!cur.customerEmail || /^email/i.test(cur.customerEmail))) {
          patch.customerEmail = p.customerEmail;
        }
        if (p.phone && !cur.phone) patch.phone = p.phone;
        if (p.address && !cur.address) patch.address = p.address;
        if (p.city && !cur.city) patch.city = p.city;
        // Items may exist but be WRONG (older parsers mangled quantities on
        // tab-glued rows) — this card is unclaimed email-sync, untouched by a
        // human, so a differing fresh parse is a correction, not a clobber.
        if (p.itemsSummary && p.itemsSummary !== cur.itemsSummary) patch.itemsSummary = p.itemsSummary;
        if (p.total > 0 && cur.total !== p.total) patch.total = p.total;
        // The type follows the CURRENT rule, not the one in force when the card
        // was minted (the rule changed four times on 2026-09-18 and every
        // earlier card kept its stale tag). An unclaimed email-sync card is
        // re-typed on every sync unless a human set the type by hand (the
        // popup leaves a 'Delivery type set to' note).
        const typedByHand = (cur.activity || []).some((a) => /^Delivery type set to/.test(a.note || ''));
        if (p.orderType && p.orderType !== cur.orderType && !typedByHand) patch.orderType = p.orderType;
        // The payment method too (2026-10-09): a card minted before the parser knew a word — "Crypto
        // (PayerURL)" became E-TR on 2026-10-08 — is re-read the same way the type is. Only the
        // website's own Payment Method field may overrule what the card says: the loose sweep of the
        // whole mail can mistake a word in a reply, so it never rewrites an existing card.
        if (p.paymentMethod && p.paymentMethod !== cur.paymentMethod && paymentMethodFromField(em)) {
          patch.paymentMethod = p.paymentMethod;
        }
        if (Object.keys(patch).length) {
          try {
            await updateCard(prior.id, patch);
          } catch {
            /* best-effort — next sync retries */
          }
        }
      }
      continue;
    }
    const id = `ord-${normKey(p.orderNumber)}`;
    const card: Omit<BoardCard, 'id'> = {
      orderNumber: p.orderNumber,
      customerName: p.customerName,
      customerEmail: p.customerEmail,
      phone: p.phone,
      address: p.address,
      city: p.city,
      distanceKm: p.distanceKm,
      total: p.total,
      itemsSummary: p.itemsSummary,
      placedAt: em.date || Date.now(),
      stage: NEW_CARD_STAGE,
      assignedTo: null,
      assignedToName: null,
      paymentMethod: p.paymentMethod,
      // Only include when known — Firestore rejects an `undefined` field value.
      ...(p.orderType ? { orderType: p.orderType } : {}),
      createdBy: 'email-sync',
      createdAt: Date.now(),
    };
    if (USE_SUPABASE) {
      // INSERT … ON CONFLICT DO NOTHING (2026-09-21). On 2026-09-20 at 21:46
      // UTC one sync run's existence read came back empty — the request went
      // out without a session, and RLS answers that with zero rows and no
      // error — so it took eleven live, claimed, paid cards for new orders and
      // upserted over them: stage back to Unassigned, owners wiped, nothing in
      // the diary because this payload has no `activity`. Dispatch watched
      // eleven cards jump columns in four seconds.
      //
      // The existence check above is advisory; THIS is the guarantee. A create
      // that lands on an existing order is a no-op, so no wrong answer from
      // that read — an unauthenticated request, a truncated page, a cause
      // nobody has thought of — can reach a card someone is working. The
      // returned rows say whether anything was actually inserted.
      const row = { id, ...rowFromPatch(strip(card)) };
      let res = await supabase().from(TABLE).upsert(row, { onConflict: createConflict, ignoreDuplicates: true }).select('id');
      if (res.error && createConflict === 'order_key' && isSchemaGap(res.error)) {
        createConflict = 'id';
        res = await supabase().from(TABLE).upsert(row, { onConflict: 'id', ignoreDuplicates: true }).select('id');
      }
      if (res.error) throw new Error(`${TABLE} upsert: ${res.error.message}`);
      if (!res.data?.length) { existing.set(key, { id, card: { ...card, id } }); continue; }
    } else {
    await setDoc(doc(db, COL, id), card);
    }
    created.push({ ...card, id });
    // In-batch dup guard: a second email for the same order in this run finds
    // the fresh card here (and may top up empty fields via the repair pass).
    existing.set(key, { id, card: { ...card, id } });
  }
  return created;
}

// Turn new order emails into Unassigned cards. The Payment Approval page runs
// this on mount + every minute (one console at a time — the settings lock), so
// the board fills in without anyone opening the Email tab.
//
// How many of the newest inbox arrivals one run looks at (Supabase mode).
// Sized from production: the busiest day in 60 days brought 222 inbox emails,
// so 300 covers a whole day with margin — a board nobody opened all night still
// finds every order from that night on its first run. A row that has already
// minted its card costs one keyed read and nothing else (0071 refuses a second
// card for an order), so the window can be generous.
export const RECENT_INBOX_MAIL = 300;

export async function syncOrderCardsFromMail(): Promise<BoardCard[]> {
  if (USE_SUPABASE) {
    // THE NEWEST ARRIVALS, THIN (2026-09-23). Until now every run read the
    // WHOLE emails cache with every column — 5,100 rows, 12 MB, three quarters
    // of it body_html that nothing here parses — to find the handful of order
    // emails that might be new: the single biggest reader on the project
    // (~1 GB a day, 20 % of all rows read per minute). A card is only ever
    // minted from the INBOX (isNewOrderEmail refuses sent mail outright, since
    // 2026-08-18), and only the newest rows can still be new, so that is all
    // we ask for: the list columns, which keep body_text (the parser reads it)
    // and leave body_html behind. Ordered by uid — the mailbox's arrival
    // order — not by the Date header, which is the sender's clock: an email
    // that reaches the cache late still sits at the top.
    const { data, error } = await supabase()
      .from('emails')
      .select(EMAIL_LIST_COLS)
      .eq('folder', 'inbox')
      .order('uid', { ascending: false })
      .limit(RECENT_INBOX_MAIL);
    if (error) throw new Error(`emails: ${error.message}`);
    return autoCreateCardsFromEmails((data || []).map(emailFromRow));
  }
  let all = await loadCachedEmails();
  for (const folder of ['inbox', 'sent'] as EmailFolder[]) {
    const fresh = await syncFolder(folder, all);
    if (fresh.length) all = all.concat(fresh);
  }
  // Scan the WHOLE cache, not just newly-synced mail, so an order email that
  // arrived before card-creation covered the inbox still gets its Unassigned
  // card. autoCreateCardsFromEmails dedups against existing cards.
  return autoCreateCardsFromEmails(all);
}

// Back-compat alias — some callers still import the old name.
export const syncOrderCardsFromSentMail = syncOrderCardsFromMail;


// ── The Delivered archive (0071) ───────────────────────────────────────────
// Everything ever delivered, read ONLY when someone opens the archive: no
// subscription, nothing fetched until then. Built for a table that will hold
// years of orders — each read is one indexed page of thin rows.
//
//   • search — substring over search_text (order number, name, email, phone,
//     city), served by the trigram index from 0071. A dispatcher typing a
//     fragment of a five-year-old order number gets it in one read.
//   • paging — keyset on (delivered_at, id), never OFFSET: page 200 costs the
//     same as page 1.
//   • payload — the columns a row shows, never activity / emails / bodies.
//     Opening a row fetches that one card in full (fetchCardById).

export interface DeliveredRow {
  id: string;
  orderNumber: string;
  customerName: string;
  city: string;
  total: number;
  deliveredAt: number | null;
  assignedToName: string | null;
  orderType?: 'delivery' | 'shipping';
  paymentMethod?: PaymentMethod;
}

export interface DeliveredCursor { at: string; id: string }

export interface DeliveredPage {
  rows: DeliveredRow[];
  /** Hand back to the next call to continue; null when this was the last page. */
  next: DeliveredCursor | null;
}

const DELIVERED_COLUMNS = 'id, order_number, customer_name, city, total, delivered_at, assigned_to_name, order_type, payment_method';
export const DELIVERED_PAGE_SIZE = 50;

// ILIKE treats % and _ as wildcards; a typed "%" or "_" must match itself.
const escapeLike = (s: string) => s.replace(/[\\%_]/g, (m) => '\\' + m);

export async function fetchDeliveredPage(opts: {
  search?: string;
  cursor?: DeliveredCursor | null;
  limit?: number;
} = {}): Promise<DeliveredPage> {
  if (!USE_SUPABASE) return { rows: [], next: null };
  const limit = Math.max(1, Math.min(200, opts.limit || DELIVERED_PAGE_SIZE));
  const term = (opts.search || '').trim();
  let q = supabase()
    .from(TABLE)
    .select(DELIVERED_COLUMNS)
    .eq('stage', 'delivered')
    .order('delivered_at', { ascending: false, nullsFirst: false })
    .order('id', { ascending: false })
    .limit(limit + 1); // one extra tells us whether another page exists
  if (term) q = q.ilike('search_text', `%${escapeLike(term)}%`);
  if (opts.cursor) {
    // Strictly after the last row seen, in the same (delivered_at desc, id desc) order.
    q = q.or(`delivered_at.lt.${opts.cursor.at},and(delivered_at.eq.${opts.cursor.at},id.lt.${opts.cursor.id})`);
  }
  const { data, error } = await q;
  if (error) throw new Error(`${TABLE}: ${error.message}`);
  const all = (data || []) as any[];
  const page = all.slice(0, limit);
  const rows: DeliveredRow[] = page.map((r) => ({
    id: r.id,
    orderNumber: r.order_number ?? '',
    customerName: r.customer_name ?? '',
    city: r.city ?? '',
    total: numOr0(r.total),
    deliveredAt: r.delivered_at != null ? msFromISO0(r.delivered_at) : null,
    assignedToName: r.assigned_to_name ?? null,
    ...(r.order_type != null ? { orderType: r.order_type } : {}),
    ...(r.payment_method != null ? { paymentMethod: r.payment_method } : {}),
  }));
  const last = page[page.length - 1];
  const next =
    all.length > limit && last && last.delivered_at != null
      ? { at: new Date(last.delivered_at).toISOString(), id: String(last.id) }
      : null;
  return { rows, next };
}

/** How many delivered orders match (the archive's header count). One HEAD request. */
export async function countDelivered(search?: string): Promise<number> {
  if (!USE_SUPABASE) return 0;
  const term = (search || '').trim();
  let q = supabase().from(TABLE).select('id', { count: 'exact', head: true }).eq('stage', 'delivered');
  if (term) q = q.ilike('search_text', `%${escapeLike(term)}%`);
  const { count, error } = await q;
  if (error) throw new Error(`${TABLE}: ${error.message}`);
  return count || 0;
}

/** One card in full, by id — for opening an archived row, or a deep link to
 *  a card the live board no longer holds. */
/**
 * Night paste duplicate check (0096): cards created in the last 24 hours with the same total and the
 * same phone number (compared by its digits — cards store phones as they were typed). One indexed
 * filter on the total, the phone match in the browser.
 */
export async function findRecentCardsByPhoneAndTotal(phone: string, total: number, sinceMs: number = Date.now() - 24 * 3600_000): Promise<BoardCard[]> {
  const want = String(phone || '').replace(/\D/g, '').slice(-10);
  if (!USE_SUPABASE || want.length < 10 || !(total > 0)) return [];
  const { data, error } = await supabase()
    .from(TABLE)
    .select('id, order_number, customer_name, phone, total, stage, created_at, placed_at, order_type, is_night, linked_order_id, linked_order_number')
    .eq('total', total)
    .gte('created_at', new Date(sinceMs).toISOString())
    .limit(50);
  if (error) throw new Error(`${TABLE}: ${error.message}`);
  return (data || []).map(cardFromRow).filter((c) => c.phone.replace(/\D/g, '').slice(-10) === want);
}

// ── The Archive (0105, Umer 2026-09-29; a page of its own since 2026-10-06) ──────────────────
// Read a page at a time when the Archived page is open — never subscribed, never the whole archive:
// board_archive_page returns the archived cards of one board ('sd' / 'mail') or of both ('all', 0114),
// the newest archive first, keyset on (archived_at ▸ delivered_at ▸ placed_at ▸ created_at, id).

export interface ArchiveCursor { at: string; id: string }
export interface ArchivePage { rows: BoardCard[]; next: ArchiveCursor | null }
export const ARCHIVE_PAGE_SIZE = 30;
const ARCHIVE_COLUMNS =
  'id, order_number, customer_name, city, total, stage, order_type, payment_method, placed_at, created_at, delivered_at, ' +
  'archived, archived_at, paid_at, assigned_to, assigned_to_name, linked_order_id, linked_order_number, is_night, night_ref';
/** "The Archive needs 0105": the function or the column is missing on this database. */
export const ARCHIVE_NEEDS_0105 = 'The Archive column needs database update 0105, which has not been applied on this site yet.';
const needs0105 = (msg: string) => /board_archive_page|archived_at|PGRST202|does not exist|could not find the function/i.test(msg);

export async function fetchArchivePage(opts: {
  kind: 'mail' | 'sd' | 'all';
  cursor?: ArchiveCursor | null;
  search?: string;
  limit?: number;
}): Promise<ArchivePage> {
  if (!USE_SUPABASE) return { rows: [], next: null };
  const limit = Math.max(1, Math.min(50, opts.limit || ARCHIVE_PAGE_SIZE));
  const term = (opts.search || '').trim();
  const { data, error } = await supabase()
    .rpc('board_archive_page', {
      p_kind: opts.kind,
      p_before_at: opts.cursor?.at ?? null,
      p_before_id: opts.cursor?.id ?? null,
      p_limit: limit + 1, // one extra tells us whether another page exists
      p_search: term ? escapeLike(term) : null,
    })
    .select(ARCHIVE_COLUMNS);
  if (error) throw new Error(needs0105(error.message || '') ? ARCHIVE_NEEDS_0105 : `Archive: ${error.message}`);
  const all = (Array.isArray(data) ? data : []) as any[];
  const page = all.slice(0, limit);
  const last = page[page.length - 1];
  const at = last ? last.archived_at ?? last.delivered_at ?? last.placed_at ?? last.created_at : null;
  return {
    rows: page.map(cardFromRow),
    next: all.length > limit && last && at ? { at: String(at), id: String(last.id) } : null,
  };
}

/** How many cards the Archive holds, or how many match (the Archived page's header). One HEAD request. */
export async function countArchived(search?: string, before?: string): Promise<number> {
  if (!USE_SUPABASE) return 0;
  const term = (search || '').trim();
  let q = supabase().from(TABLE).select('id', { count: 'exact', head: true }).eq('archived', true);
  // History (2026-10-06): archived before the board's cutoff; an old row without archived_at is History too.
  if (before) q = q.or(`archived_at.lt.${before},archived_at.is.null`);
  if (term) q = q.ilike('search_text', `%${escapeLike(term)}%`);
  const { count, error } = await q;
  if (error) throw new Error(`${TABLE}: ${error.message}`);
  return count || 0;
}

// ── History › Cancelled (Umer, 2026-10-06) ─────────────────────────────────
// Cancelled cards older than the board's two Ottawa days (`before` = boardHistory.historyCutoffIso): the
// Cancelled column holds the newer ones. Read a page at a time on open — never subscribed. Newest
// cancellation first, keyset on (cancelled_at, id). Archived cancelled cards are History › Archived, not
// here (archived = false), so a card is never on both. Before 0113 there is no cancelled_at: the first
// page lists by when the order was placed, without paging, and says nothing is broken.
const CANCELLED_COLUMNS = ARCHIVE_COLUMNS + ', cancelled_at';
export interface CancelledCursor { at: string; id: string }
export interface CancelledPage { rows: BoardCard[]; next: CancelledCursor | null }
export const CANCELLED_PAGE_SIZE = 50;
const needs0113 = (msg: string) => /cancelled_at/i.test(msg) && /does not exist|could not find/i.test(msg);

export async function fetchCancelledPage(opts: {
  cursor?: CancelledCursor | null;
  search?: string;
  limit?: number;
  /** ISO: only cards cancelled before this (the board's cutoff); absent = every live cancelled card. */
  before?: string;
} = {}): Promise<CancelledPage> {
  if (!USE_SUPABASE) return { rows: [], next: null };
  const limit = Math.max(1, Math.min(200, opts.limit || CANCELLED_PAGE_SIZE));
  const term = (opts.search || '').trim();
  let q = supabase()
    .from(TABLE)
    .select(CANCELLED_COLUMNS)
    .eq('stage', 'cancelled')
    .eq('archived', false)
    .order('cancelled_at', { ascending: false, nullsFirst: false })
    .order('id', { ascending: false })
    .limit(limit + 1); // one extra tells us whether another page exists
  if (term) q = q.ilike('search_text', `%${escapeLike(term)}%`);
  if (opts.before) q = q.lt('cancelled_at', opts.before);
  if (opts.cursor) {
    // Strictly after the last row seen, in the same (cancelled_at desc, id desc) order.
    q = q.or(`cancelled_at.lt.${opts.cursor.at},and(cancelled_at.eq.${opts.cursor.at},id.lt.${opts.cursor.id})`);
  }
  const { data, error } = await q;
  if (error) {
    if (!needs0113(error.message || '')) throw new Error(`${TABLE}: ${error.message}`);
    // Pre-0113: no clock to page on — the first page, newest placed first, is the whole list.
    let f = supabase().from(TABLE).select(ARCHIVE_COLUMNS).eq('stage', 'cancelled').eq('archived', false)
      .order('placed_at', { ascending: false, nullsFirst: false }).limit(limit);
    if (term) f = f.ilike('search_text', `%${escapeLike(term)}%`);
    if (opts.before) f = f.lt('placed_at', opts.before);
    const r = await f;
    if (r.error) throw new Error(`${TABLE}: ${r.error.message}`);
    return { rows: ((r.data || []) as any[]).map(cardFromRow), next: null };
  }
  const all = (data || []) as any[];
  const page = all.slice(0, limit);
  const last = page[page.length - 1];
  return {
    rows: page.map(cardFromRow),
    next: all.length > limit && last && last.cancelled_at != null
      ? { at: new Date(last.cancelled_at).toISOString(), id: String(last.id) }
      : null,
  };
}

/** How many cards wait on the Cancelled page, or how many match. One HEAD request. */
export async function countCancelled(search?: string, before?: string): Promise<number> {
  if (!USE_SUPABASE) return 0;
  const term = (search || '').trim();
  let q = supabase().from(TABLE).select('id', { count: 'exact', head: true }).eq('stage', 'cancelled').eq('archived', false);
  if (before) q = q.lt('cancelled_at', before);
  if (term) q = q.ilike('search_text', `%${escapeLike(term)}%`);
  const { count, error } = await q;
  if (error) throw new Error(`${TABLE}: ${error.message}`);
  return count || 0;
}

// ── Mail orders cancelled since a date (the weekly non-payment count, 2026-10-30) ──
// Every cancelled mail card since `sinceMs`, archived or not (a cancelled mail card keeps stage 'cancelled' in the
// Archive, 0113): only the two stamps the count needs. An untyped card counts as mail, as the board reads it.
// Postgres only — the Firestore path is frozen (docs/retirement/firestore.md).
export async function fetchMailCancelsSince(sinceMs: number): Promise<Array<{ cancelledAt: number | null; paidAt: number | null }>> {
  if (!USE_SUPABASE) return [];
  const rows = await fetchAllRows<any>(() => supabase()
    .from(TABLE)
    .select('id, cancelled_at, paid_at, order_type')
    .eq('stage', 'cancelled')
    .gte('cancelled_at', new Date(sinceMs).toISOString())
    .or('order_type.is.null,order_type.neq.delivery')
    .order('id'));
  return rows.map((r) => ({
    cancelledAt: r.cancelled_at == null ? null : msFromISO0(r.cancelled_at),
    paidAt: r.paid_at == null ? null : msFromISO0(r.paid_at),
  }));
}

// ── The board's search reaches the finished orders (Umer, 2026-10-06) ──────
// The board holds its live cards only. A search typed on it asks the database ONCE for the cards that
// are not on it — in the Archive, or cancelled — so an old order number is found from the board too.
// Thin rows, newest placed first, a dozen at most; opening one fetches the card in full (fetchCardById).
export const FINISHED_SEARCH_LIMIT = 12;
export async function searchFinishedCards(term: string, limit: number = FINISHED_SEARCH_LIMIT): Promise<BoardCard[]> {
  const t = (term || '').trim();
  if (!USE_SUPABASE || t.length < 2) return [];
  const { data, error } = await supabase()
    .from(TABLE)
    .select(ARCHIVE_COLUMNS)
    .or('archived.eq.true,stage.eq.cancelled')
    .ilike('search_text', `%${escapeLike(t)}%`)
    .order('placed_at', { ascending: false, nullsFirst: false })
    .limit(Math.max(1, Math.min(50, limit)));
  if (error) throw new Error(`${TABLE}: ${error.message}`);
  return ((data || []) as any[]).map(cardFromRow);
}

/** To the Archive, or back on the board. The database keeps an order's card on the board until the
 *  order is delivered or cancelled (0105); before 0105 only a delivered card stays archived — the row
 *  the write returns shows which, so the diary line is added only once the move really landed. */
export async function setCardArchived(id: string, archived: boolean, entry: CardActivity): Promise<void> {
  if (!USE_SUPABASE) throw new Error('The Archive needs the Supabase backend.');
  const { data: rows, error } = await supabase()
    .from(TABLE)
    .update({ archived })
    .eq('id', id)
    .select('id, archived');
  if (error) throw new Error(String(error.message || '').replace(/^order_card:\s*/, '') || `${TABLE} update failed`);
  const row = Array.isArray(rows) ? rows[0] : null;
  if (!row) throw new Error('That card is not on the board any more.');
  if (!!row.archived !== archived) throw new Error(ARCHIVE_NEEDS_0105);
  await logCardActivity(id, entry); // best-effort, like every diary line
}

export async function fetchCardById(id: string): Promise<BoardCard | null> {
  if (!USE_SUPABASE) return null;
  const { data, error } = await supabase().from(TABLE).select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(`${TABLE}: ${error.message}`);
  return data ? cardFromRow(data) : null;
}

/** One card, live (its own row only) — the board popup opened in place on the Orders page (0097)
 *  reads this instead of the whole board. null = no such card. Supabase only. */
export function subscribeCard(id: string, onCard: (card: BoardCard | null) => void, onError?: (e: Error) => void): () => void {
  if (!USE_SUPABASE) { onCard(null); return () => {}; }
  return subscribeRow(TABLE, { column: 'id', value: id }, (r) => (r ? cardFromRow(r) : null), onCard, { onError, client: supabase() });
}
